import React, { useState, useEffect } from 'react';

interface APISpec {
  openapi: string;
  info: {
    title: string;
    version: string;
    description: string;
    contact: {
      name: string;
      url: string;
    };
  };
  servers: Array<{
    url: string;
    description: string;
  }>;
  paths: Record<string, any>;
  quickStart: {
    message: string;
    description: string;
    features: string[];
    quickTest: Record<string, string>;
    frontend: string;
    documentation: string;
  };
}

export const APIDocsPage: React.FC = () => {
  const [apiSpec, setApiSpec] = useState<APISpec | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedEndpoint, setSelectedEndpoint] = useState<string | null>(null);

  useEffect(() => {
    fetchAPISpec();
  }, []);

  const fetchAPISpec = async () => {
    try {
      const response = await fetch('https://cf.qrsp.app/');
      const data = await response.json();
      setApiSpec(data);
    } catch (error) {
      console.error('Failed to fetch API spec:', error);
    } finally {
      setLoading(false);
    }
  };

  const testEndpoint = async (endpoint: string, method: string, body?: any) => {
    try {
      const fullUrl = endpoint.startsWith('http') ? endpoint : `https://cf.qrsp.app${endpoint}`;
      const options: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
      };

      if (body) {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(fullUrl, options);
      const data = await response.json();
      
      console.log(`${method} ${endpoint}:`, data);
      alert(`✅ Response logged to console\nStatus: ${response.status}\nCheck browser console for full response`);
    } catch (error) {
      console.error('Test failed:', error);
      alert(`❌ Test failed: ${error}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading API documentation...</p>
        </div>
      </div>
    );
  }

  if (!apiSpec) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">📡</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Cannot Load API Docs</h2>
          <p className="text-gray-600 mb-4">Unable to fetch API documentation from the server.</p>
          <button
            onClick={fetchAPISpec}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const endpoints = Object.keys(apiSpec.paths || {});

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-8 mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="text-4xl">📡</div>
            <div>
              <h1 className="text-3xl font-bold">{apiSpec.info.title}</h1>
              <p className="text-blue-100">Version {apiSpec.info.version}</p>
            </div>
          </div>
          <p className="text-xl opacity-90 mb-4">{apiSpec.info.description}</p>
          <div className="flex items-center space-x-6 text-sm">
            <span className="bg-blue-500 bg-opacity-50 px-3 py-1 rounded">
              OpenAPI 3.0
            </span>
            <span className="bg-purple-500 bg-opacity-50 px-3 py-1 rounded">
              JWT Secured
            </span>
            <span className="bg-green-500 bg-opacity-50 px-3 py-1 rounded">
              Production Ready
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Quick Start */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-4">
              <h3 className="text-lg font-semibold mb-4">🚀 Quick Start</h3>
              
              <div className="space-y-4 mb-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Features:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {apiSpec.quickStart.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Base URL:</h4>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded block">
                    {apiSpec.servers[0]?.url}
                  </code>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Quick Tests:</h4>
                {Object.entries(apiSpec.quickStart.quickTest).map(([name, endpoint]) => (
                  <button
                    key={name}
                    onClick={() => {
                      if (endpoint.includes('POST')) {
                        const [method, path] = endpoint.split(' ');
                        if (path.includes('generate')) {
                          testEndpoint('/api/qr/generate', 'POST', { coupon_id: 1 });
                        } else if (path.includes('validate')) {
                          alert('ℹ️ First generate a QR code to get a token, then test validation');
                        }
                      } else {
                        const [method, path] = endpoint.split(' ');
                        testEndpoint(path, method);
                      }
                    }}
                    className="w-full text-left text-xs bg-gray-50 hover:bg-gray-100 p-2 rounded border transition-colors"
                  >
                    <div className="font-mono">{name}</div>
                    <div className="text-gray-600">{endpoint}</div>
                  </button>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t">
                <a
                  href={apiSpec.quickStart.frontend}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded text-center block hover:bg-blue-700 transition-colors"
                >
                  🌐 Open Frontend App
                </a>
              </div>
            </div>
          </div>

          {/* Endpoints */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">API Endpoints</h2>
              
              {endpoints.map((path) => {
                const pathSpec = apiSpec.paths[path];
                const methods = Object.keys(pathSpec);
                
                return (
                  <div key={path} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    <div className="bg-gray-50 px-6 py-4 border-b">
                      <h3 className="text-lg font-semibold text-gray-900">{path}</h3>
                    </div>
                    
                    <div className="divide-y">
                      {methods.map((method) => {
                        const methodSpec = pathSpec[method];
                        const methodColor = method === 'get' ? 'green' : method === 'post' ? 'blue' : 'gray';
                        
                        return (
                          <div key={method} className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <span className={`px-2 py-1 rounded text-xs font-bold uppercase bg-${methodColor}-100 text-${methodColor}-800`}>
                                  {method}
                                </span>
                                <h4 className="text-lg font-medium">{methodSpec.summary}</h4>
                              </div>
                              
                              <button
                                onClick={() => {
                                  const endpointKey = `${method.toUpperCase()} ${path}`;
                                  if (method === 'get') {
                                    testEndpoint(path, method.toUpperCase());
                                  } else if (method === 'post') {
                                    if (path.includes('generate')) {
                                      testEndpoint(path, method.toUpperCase(), { coupon_id: 1 });
                                    } else if (path.includes('validate')) {
                                      alert('ℹ️ First generate a QR code to get a token');
                                    }
                                  }
                                }}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm transition-colors"
                              >
                                🧪 Test
                              </button>
                            </div>
                            
                            <p className="text-gray-600 mb-4">{methodSpec.description}</p>
                            
                            {/* Request Body */}
                            {methodSpec.requestBody && (
                              <div className="mb-4">
                                <h5 className="font-medium text-gray-900 mb-2">Request Body:</h5>
                                <div className="bg-gray-50 rounded p-3">
                                  <pre className="text-sm text-gray-700 overflow-x-auto">
                                    {JSON.stringify(methodSpec.requestBody.content['application/json'].schema, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            )}
                            
                            {/* Response Example */}
                            <div>
                              <h5 className="font-medium text-gray-900 mb-2">Example Response:</h5>
                              <div className="bg-gray-50 rounded p-3">
                                <pre className="text-sm text-gray-700 overflow-x-auto">
                                  {JSON.stringify(
                                    methodSpec.responses['200']?.content?.['application/json']?.example || 
                                    { message: "Success" }, 
                                    null, 
                                    2
                                  )}
                                </pre>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">📊 Database Schema</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="bg-gray-50 rounded p-3">
              <h4 className="font-medium text-gray-900 mb-2">🚗 Drivers</h4>
              <ul className="text-gray-600 space-y-1">
                <li>• name, phone</li>
                <li>• pix_key (payments)</li>
                <li>• recruit vendors</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <h4 className="font-medium text-gray-900 mb-2">🏪 Vendors</h4>
              <ul className="text-gray-600 space-y-1">
                <li>• name, phone, address</li>
                <li>• category (restaurant, etc.)</li>
                <li>• linked to driver</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <h4 className="font-medium text-gray-900 mb-2">🎫 Coupons</h4>
              <ul className="text-gray-600 space-y-1">
                <li>• title, discount</li>
                <li>• usage tracking</li>
                <li>• expiration dates</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};